import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './academy.css';
import { AcademyProvider, useAcademy } from './AcademyContext';
import AcademyNav from './components/AcademyNav';
import AcademyGate from './components/AcademyGate';
import AcademyOnboarding from './components/AcademyOnboarding';
import PageTransition from './components/PageTransition';
import AcademyHome from './pages/AcademyHome';
import TopicPage from './pages/TopicPage';
import CoursePage from './pages/CoursePage';
import LessonPage from './pages/LessonPage';
import QuizPage from './pages/QuizPage';
import PracticeLab from './pages/PracticeLab';

/**
 * Academy — public version.
 *
 * Content is loaded live from the database (managed via the Team Dashboard
 * CMS). No static fallback. Routes are relative to the mount point
 * (`/academy/*`).
 */
const AcademyShell = () => {
  const location = useLocation();
  const { pathname } = location;
  const { loading, member, memberLoading, needsProfile } = useAcademy();

  // Exercise routes run in a focused, distraction-free mode: hide the full nav
  // so the user can only close the exercise (X), never bounce back to the home.
  const isFocusMode = /\/(quiz|practice)\/?$/.test(pathname);

  // Navigation direction drives the page transition. Forward moves (e.g.
  // submitting an exercise → next step) glide in sideways; everything else uses
  // the default vertical settle. Set via navigate(to, { state: { dir } }).
  const direction = location.state?.dir === 'forward' ? 'forward' : 'default';

  // Validate the stored access key before showing anything.
  if (memberLoading) {
    return (
      <div className="ac-root">
        <div className="ac-boot">Lädt…</div>
      </div>
    );
  }

  // No valid member session → secret-key gate.
  if (!member) {
    return (
      <div className="ac-root ac-root-focus">
        <AcademyGate />
      </div>
    );
  }

  // Member created with just a key → capture name/email before entering.
  if (needsProfile) {
    return (
      <div className="ac-root ac-root-focus">
        <AcademyOnboarding />
      </div>
    );
  }

  // Gate routing until content has loaded so pages don't redirect prematurely
  // (a not-yet-loaded topic would otherwise look "not found").
  if (loading) {
    return (
      <div className="ac-root">
        <div className="ac-boot">Lädt…</div>
      </div>
    );
  }

  return (
    <div className={`ac-root${isFocusMode ? ' ac-root-focus' : ''}`}>
      {!isFocusMode && <AcademyNav />}
      <AnimatePresence mode="wait" initial={false} custom={direction}>
        <PageTransition key={pathname} direction={direction}>
          <Routes location={location}>
            <Route path="/" element={<AcademyHome />} />
            <Route path=":topicId" element={<TopicPage />} />
            <Route path=":topicId/:courseId" element={<CoursePage />} />
            <Route path=":topicId/:courseId/:lessonId" element={<LessonPage />} />
            <Route path=":topicId/:courseId/:lessonId/quiz" element={<QuizPage />} />
            <Route path=":topicId/:courseId/:lessonId/practice" element={<PracticeLab />} />
            <Route path="*" element={<Navigate to="/academy" replace />} />
          </Routes>
        </PageTransition>
      </AnimatePresence>
    </div>
  );
};

const AcademyApp = () => (
  <AcademyProvider>
    <AcademyShell />
  </AcademyProvider>
);

export default AcademyApp;
